
import Link from '@/components/compat/Link';
import { Eye, Flame, Layers, Pencil, Plus, Rocket, Sparkles, Tag, Wrench } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelSurfaceClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import type { ChangelogEntry } from '@/lib/changelog/changelog-types';
import { useChangelogSources } from '@/hooks/use-changelog-sources';
import { changelogVersionHref } from '@/components/painel/changelog/changelog-nav-config';
import { changelogPublicHref } from '@/components/painel/changelog/changelog-painel-utils';
import { ChangelogProductTabs } from '@/components/painel/changelog/changelog-product';
import { ChangelogBadge, ChangelogPageShell } from '@/components/painel/changelog/changelog-ui';

function countSectionItems(entry: ChangelogEntry): number {
  return entry.sections.reduce((acc, s) => acc + s.items.length, 0);
}

function countByType(entry: ChangelogEntry, type: ChangelogEntry['sections'][number]['type']): number {
  const sec = entry.sections.find((s) => s.type === type);
  return sec ? sec.items.length : 0;
}

type PainelChangelogOverviewPageProps = {
  product: ChangelogProductId;
};

export function PainelChangelogOverviewPage({ product }: PainelChangelogOverviewPageProps) {
  const { sources, loading } = useChangelogSources();
  const source = sources[product];
  const changelog = source.entries;
  const latestEntry = useMemo(() => changelog.find((e) => e.latest) ?? changelog[0], [changelog]);
  const totalBullets = useMemo(() => changelog.reduce((acc, e) => acc + countSectionItems(e), 0), [changelog]);
  const recent = useMemo(() => changelog.slice(0, 5), [changelog]);

  const uniqueSectionLabels = useMemo(() => {
    const set = new Set<string>();
    for (const e of changelog) {
      for (const s of e.sections) set.add(s.label);
    }
    return set.size;
  }, [changelog]);

  return (
    <ChangelogPageShell className="space-y-8 pb-10">
      <ChangelogProductTabs product={product} className="mb-2" />

      <div
        className={cn(
          painelSurfaceClass,
          'relative overflow-hidden border-sky-400/20 bg-gradient-to-br from-blue-950/60 via-[#05051f]/90 to-[#000b6a]/50 p-8 shadow-[0_0_60px_-24px_rgba(56,189,248,0.25)]'
        )}
      >
        <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl space-y-3">
            <p className="font-inter text-[10px] font-bold uppercase tracking-[0.22em] text-sky-300/55">
              Changelogs · {source.label}
            </p>
            <h1 className="font-poppins text-3xl font-bold tracking-tight text-white md:text-4xl">
              Central de atualizações
            </h1>
            <p className="font-inter text-base text-slate-400">
              Os mesmos registros da página pública (
              <Link href={changelogPublicHref(product)} className="text-sky-300 hover:underline">
                /changelog
              </Link>
              )
              {loading ? ' — a sincronizar…' : null}.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                asChild
                className="rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 font-poppins font-semibold shadow-lg shadow-sky-950/40"
              >
                <Link href={`/painel/changelog/${product}/versions/novo`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nova versão
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl border-white/15 bg-white/[0.06] font-poppins text-blue-50 hover:bg-white/10">
                <Link href={`/painel/changelog/${product}/versions`}>
                  Ver todas as versões
                </Link>
              </Button>
            </div>
          </div>
          <div className="flex shrink-0 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-right backdrop-blur-md">
              <p className="font-poppins text-[10px] uppercase tracking-wider text-slate-400">Fonte</p>
              <p className="mt-1 font-poppins text-xl font-bold tabular-nums text-white">{source.label}</p>
              <p className="font-inter text-[11px] text-slate-400">Site + painel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <PainelStatCard
          title="Total de versões"
          value={String(changelog.length)}
          icon={Layers}
          variant="sky"
          trend={{ label: `Changelog ${source.shortLabel}`, positive: true }}
        />
        <PainelStatCard
          title="Versão atual (site)"
          value={latestEntry ? `v${latestEntry.version}` : '—'}
          icon={Rocket}
          variant="cyan"
          trend={{ label: latestEntry?.date ?? '', positive: true }}
        />
        <PainelStatCard
          title="Bullets no changelog"
          value={String(totalBullets)}
          icon={Tag}
          variant="indigo"
          trend={{ label: 'Soma de todas as versões', positive: true }}
        />
        <PainelStatCard
          title="Correções · v atual"
          value={String(latestEntry ? countByType(latestEntry, 'fix') : 0)}
          icon={Wrench}
          variant="violet"
          trend={{ label: 'Tipo fix na última release', positive: true }}
        />
        <PainelStatCard
          title="Novidades · v atual"
          value={String(latestEntry ? countByType(latestEntry, 'new') : 0)}
          icon={Sparkles}
          variant="sky"
        />
        <PainelStatCard
          title="Rótulos de secção"
          value={String(uniqueSectionLabels)}
          icon={Layers}
          description="Distintos em todo o histórico"
          variant="cyan"
        />
      </div>

      <div className={cn(painelSurfaceClass, 'overflow-hidden p-6')}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-poppins text-lg font-bold text-white">Lançamentos recentes</h2>
            <p className="font-inter text-sm text-slate-400">Primeiras entradas do array público (ordem editorial).</p>
          </div>
          <Button asChild variant="ghost" className="text-sky-300 hover:bg-white/10 hover:text-sky-100">
            <Link href={`/painel/changelog/${product}/versions`}>Gerenciar todas</Link>
          </Button>
        </div>
        <ul className="mt-6 divide-y divide-white/[0.06]">
          {recent.map((r) => (
            <li
              key={r.version}
              className="flex flex-col gap-3 py-5 first:pt-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-poppins text-lg font-bold text-white">v{r.version}</span>
                  <ChangelogBadge tone="live">Publicado</ChangelogBadge>
                  {r.latest ? (
                    <ChangelogBadge tone="vip">
                      <span className="inline-flex items-center gap-1">
                        <Flame className="h-3 w-3" /> Atual no site
                      </span>
                    </ChangelogBadge>
                  ) : null}
                  <span className="font-inter text-xs text-slate-400">{r.date}</span>
                </div>
                <p className="font-inter text-sm text-slate-400">
                  {r.sections.length} secções · {countSectionItems(r)} itens
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button
                  asChild
                  size="sm"
                  className="rounded-xl border border-white/10 bg-white/[0.06] font-poppins text-xs hover:bg-white/10"
                  variant="outline"
                >
                  <Link href={changelogVersionHref(product, r.version)}>
                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                    Ver
                  </Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="rounded-xl border border-white/10 bg-white/[0.06] font-poppins text-xs hover:bg-white/10"
                  variant="outline"
                >
                  <Link href={`${changelogVersionHref(product, r.version)}/edit`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Editar
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </ChangelogPageShell>
  );
}
